import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Form, Modal, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useRoleControl } from '../helpers/roleControl';

const { Title, Text } = Typography;

const SHARE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SavedReport {
    id: number;
    title: string;
    reportType?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    createdAt?: string;
    area?: {
        id: number;
        name: string;
    };
    template?: {
        id: number;
        name?: string;
        key?: string;
    };
}

const SavedReports: React.FC = () => {
    const navigate = useNavigate();
    const canShareReports = useRoleControl(['admin']);
    const [reports, setReports] = useState<SavedReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareReport, setShareReport] = useState<SavedReport | null>(null);
    const [shareForm] = Form.useForm();
    const [companyUsers, setCompanyUsers] = useState<Array<{ id: number; email: string }>>([]);
    const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);
    const [loadingShares, setLoadingShares] = useState(false);
    const [savingShares, setSavingShares] = useState(false);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        axios
            .get(ENDPOINTS.REPORTS, { withCredentials: true })
            .then((res) => {
                if (!mounted) return;
                setReports(res.data?.data || []);
            })
            .catch((err) => {
                console.error('Failed to load saved reports:', err);
                message.error('Failed to load saved reports');
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!shareModalOpen || !canShareReports || !shareReport) return;
        let mounted = true;

        const loadShareData = async () => {
            setLoadingShares(true);
            try {
                let users = companyUsers;
                if (users.length === 0) {
                    setLoadingCompanyUsers(true);
                    const usersRes = await axios.get(ENDPOINTS.GET_COMPANY_USERS, { withCredentials: true });
                    users = usersRes.data?.data ?? usersRes.data ?? [];
                    if (mounted) {
                        setCompanyUsers(Array.isArray(users) ? users : []);
                    }
                }

                const sharesRes = await axios.get(ENDPOINTS.REPORT_SHARES(shareReport.id), {
                    withCredentials: true,
                });
                const shares = sharesRes.data?.data ?? sharesRes.data ?? [];
                const userIdSet = new Set(
                    Array.isArray(users) ? users.map((user: { id: number }) => user.id) : []
                );
                const shareUserIds = Array.isArray(shares)
                    ? shares
                          .filter((share: any) => share.sharedWithUserId && userIdSet.has(share.sharedWithUserId))
                          .map((share: any) => share.sharedWithUserId)
                    : [];
                const shareEmails = Array.isArray(shares)
                    ? shares
                          .filter((share: any) =>
                              share.sharedWithEmail &&
                              (!share.sharedWithUserId || !userIdSet.has(share.sharedWithUserId))
                          )
                          .map((share: any) => String(share.sharedWithEmail).toLowerCase())
                    : [];

                if (mounted) {
                    shareForm.setFieldsValue({
                        shareUserIds: Array.from(new Set(shareUserIds)),
                        shareEmails: Array.from(new Set(shareEmails)),
                    });
                }
            } catch (err) {
                console.error('Failed to load share details:', err);
                message.error('Failed to load share details');
            } finally {
                if (mounted) {
                    setLoadingShares(false);
                    setLoadingCompanyUsers(false);
                }
            }
        };

        loadShareData();
        return () => {
            mounted = false;
        };
    }, [canShareReports, companyUsers, shareForm, shareModalOpen, shareReport]);

    const handleCloseShareModal = () => {
        setShareModalOpen(false);
        setShareReport(null);
        shareForm.resetFields();
    };

    const handleSaveShares = async () => {
        if (!shareReport) return;
        try {
            const values = await shareForm.validateFields();
            setSavingShares(true);

            const shareUserIds = (values.shareUserIds || [])
                .map((id: number) => Number(id))
                .filter((id: number) => !Number.isNaN(id));
            const shareEmails = (values.shareEmails || [])
                .map((email: string) => String(email).trim().toLowerCase())
                .filter((email: string) => email.length > 0);

            const payload: Record<string, any> = {
                shareWithUserIds: Array.from(new Set(shareUserIds)),
                shareWithEmails: Array.from(new Set(shareEmails)),
            };

            const response = await axios.put(
                ENDPOINTS.REPORT_SHARES(shareReport.id),
                payload,
                { withCredentials: true }
            );
            message.success('Sharing updated');
            const shareWarnings = response.data?.shareWarnings;
            if (Array.isArray(shareWarnings) && shareWarnings.length > 0) {
                message.warning(shareWarnings[0]);
            }
            handleCloseShareModal();
        } catch (err: any) {
            if (err?.errorFields) {
                return;
            }
            console.error('Failed to update sharing:', err);
            message.error('Failed to update sharing');
        } finally {
            setSavingShares(false);
        }
    };

    const openShareModal = useCallback((report: SavedReport) => {
        setShareReport(report);
        shareForm.setFieldsValue({ shareUserIds: [], shareEmails: [] });
        setShareModalOpen(true);
    }, [shareForm]);

    const columns = useMemo(
        () => {
            const baseColumns: any[] = [
            {
                title: 'Title',
                dataIndex: 'title',
                key: 'title',
                render: (_: string, record: SavedReport) => (
                    <Button
                        type="link"
                        className="saved-reports-title-link"
                        onClick={() => navigate(`/saved-reports/${record.id}`)}
                    >
                        {record.title || `Report ${record.id}`}
                    </Button>
                ),
            },
            {
                title: 'Area',
                dataIndex: ['area', 'name'],
                key: 'area',
                render: (_: string, record: SavedReport) => record.area?.name || '—',
            },
            {
                title: 'Date Range',
                key: 'dateRange',
                render: (_: string, record: SavedReport) => {
                    if (!record.dateFrom || !record.dateTo) return '—';
                    return `${dayjs(record.dateFrom).format('YYYY-MM-DD')} → ${dayjs(record.dateTo).format('YYYY-MM-DD')}`;
                },
            },
            {
                title: 'Template',
                key: 'template',
                render: (_: string, record: SavedReport) =>
                    record.template?.name || record.template?.key || record.reportType || '—',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (value: string | undefined) => (
                    <Tag color={value === 'published' ? 'green' : value === 'archived' ? 'default' : 'blue'}>
                        {value || 'draft'}
                    </Tag>
                ),
            },
            {
                title: 'Created',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (value: string | undefined) => (value ? dayjs(value).format('YYYY-MM-DD') : '—'),
            },
            ];

            if (canShareReports) {
                baseColumns.push({
                    title: 'Sharing',
                    key: 'sharing',
                    render: (_: string, record: SavedReport) => (
                        <Button
                            type="link"
                            className="saved-reports-sharing-manage"
                            onClick={() => openShareModal(record)}
                        >
                            Manage
                        </Button>
                    ),
                });
            }

            return baseColumns;
        },
        [canShareReports, navigate, openShareModal]
    );

    return (
        <div className="saved-reports-page" style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} className="saved-reports-header">
                <Space direction="vertical" size={0}>
                    <Title level={2} style={{ margin: 0 }}>Saved Reports</Title>
                    <Text type="secondary">View and reopen saved report runs.</Text>
                </Space>
            </Row>
            <Card className="saved-reports-card">
                {reports.length === 0 && !loading ? (
                    <Empty description="No saved reports yet." />
                ) : (
                    <Table
                        rowKey="id"
                        className="saved-reports-table"
                        loading={loading}
                        dataSource={reports}
                        columns={columns}
                        pagination={{ pageSize: 10 }}
                    />
                )}
                <div className="list-create-row">
                    <Button
                        type="text"
                        icon={<PlusOutlined />}
                        className="saved-reports-generate-button list-create-button"
                        onClick={() => navigate('/reports')}
                    >
                        Generate report
                    </Button>
                </div>
            </Card>
            {canShareReports && (
                <Modal
                    title="Manage report sharing"
                    className="saved-reports-share-modal"
                    open={shareModalOpen}
                    onCancel={handleCloseShareModal}
                    onOk={handleSaveShares}
                    okText="Save Sharing"
                    confirmLoading={savingShares}
                    okButtonProps={{ disabled: loadingShares }}
                >
                    <Form form={shareForm} layout="vertical">
                        <Form.Item label="Share with company users" name="shareUserIds">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select users to share with"
                                loading={loadingCompanyUsers}
                                optionFilterProp="children"
                            >
                                {companyUsers.map((user) => (
                                    <Select.Option key={user.id} value={user.id}>
                                        {user.email}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Invite by email"
                            name="shareEmails"
                            rules={[
                                {
                                    validator: (_rule, value: string[]) => {
                                        if (!value || value.length === 0) {
                                            return Promise.resolve();
                                        }
                                        const normalized = value
                                            .map((email) => String(email).trim())
                                            .filter((email) => email.length > 0);
                                        const invalid = normalized.filter(
                                            (email) => !SHARE_EMAIL_REGEX.test(email)
                                        );
                                        if (invalid.length > 0) {
                                            return Promise.reject(new Error('Enter valid email addresses'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Select
                                mode="tags"
                                tokenSeparators={[',', ' ']}
                                placeholder="name@example.com"
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </div>
    );
};

export default SavedReports;
